import type {IPerson, ITask, TTaskStatus} from '../../../types'
import {dateUtil} from '../../../utils/dateUtil'
import {taskConstants, TASK_STATUS} from '../constants/taskConstants'
import type {
  ITaskRow,
  ITeamMemberSummary,
  TTaskStatusFilter,
  TTeamMemberState,
} from '../types/taskScreenTypes'

/** Grouping key for tasks with no `responsible`. Not an email, so it cannot
 *  collide with a real owner. */
export const UNASSIGNED_OWNER_KEY = '__unassigned__'

const STATUS_RANK: Record<TTaskStatus, number> = {
  [TASK_STATUS.pending]: 0,
  [TASK_STATUS.inProgress]: 1,
  [TASK_STATUS.completed]: 2,
  [TASK_STATUS.closed]: 3,
}

/**
 * The `tasks.status` column is a plain STRING with no CHECK constraint, and at
 * least one writer sends `'PENDING'` (Ideation's actionable-insight flow). Fold
 * anything unrecognised to Pending rather than rendering a blank pill.
 */
const normalizeTaskStatus = (status?: string): TTaskStatus => {
  if (!status) return TASK_STATUS.pending
  const match = taskConstants.STATUS_ORDER.find(
    known => known.toLowerCase() === status.trim().toLowerCase(),
  )
  return match ?? TASK_STATUS.pending
}

/** `responsible` arrives as a single object from `GET /tasks` (the repository
 *  flattens it with `JSON_EXTRACT $[0]`), but the column stores an array and
 *  `PUT /tasks` writes one. Accept both shapes on read. */
const readOwner = (responsible: ITask['responsible']): IPerson | undefined => {
  const candidate = Array.isArray(responsible) ? responsible[0] : responsible
  if (!candidate) return undefined
  const person = candidate as IPerson
  return person.emailAddress || person.username ? person : undefined
}

interface IDecorateContext {
  /** Signed-in user's email, for the "You" marker. */
  currentUserEmail?: string
  /** Injected so the caller can hold one `new Date()` for a whole list render,
   *  and so tests can pin the clock. */
  now?: Date
}

const decorateTask = (task: ITask, ctx: IDecorateContext = {}): ITaskRow => {
  const now = ctx.now ?? new Date()
  const status = normalizeTaskStatus(task.status)
  const isTerminal = taskConstants.TERMINAL_STATUSES.includes(status)
  const owner = readOwner(task.responsible)
  const ownerEmail = owner?.emailAddress ?? ''

  // dueDate is a UTC-anchored calendar day (see dateUtil.toUtcCalendarDay), so
  // the comparison has to measure on the UTC calendar too — a local-time
  // difference flips overdue on and off around midnight.
  const daysPastDue = task.dueDate
    ? dateUtil.differenceInUtcDays(now, task.dueDate)
    : 0
  const daysSinceUpdate = task.updatedAt
    ? Math.max(0, dateUtil.differenceInUtcDays(now, task.updatedAt))
    : 0

  return {
    task,
    id: String(task.id ?? ''),
    name: task.name,
    status,
    commentCount: task.commentCount ?? 0,
    statusLabelKey: taskConstants.STATUS_META[status].labelKey,
    statusIcon: taskConstants.STATUS_META[status].icon,
    isTerminal,
    isOverdue: !isTerminal && daysPastDue > 0,
    daysPastDue: Math.max(0, daysPastDue),
    isStale: !isTerminal && daysSinceUpdate >= taskConstants.STALE_DAYS,
    isMine:
      !!ownerEmail &&
      !!ctx.currentUserEmail &&
      ownerEmail.toLowerCase() === ctx.currentUserEmail.toLowerCase(),
    daysSinceUpdate,
    dueDate: task.dueDate,
    // `completed_at` defaults to NOW server-side, so every row carries a
    // timestamp whether or not it was ever completed. Only surface it once the
    // status actually says Completed.
    completedAt:
      status === TASK_STATUS.completed ? task.completed_at : undefined,
    owner,
    ownerEmail,
    ownerName: owner?.username || owner?.emailAddress || '',
    contributors: task.people ?? [],
    isBlocked: !!task.isBlocked,
    blockedReason: task.blockedReason ?? null,
  }
}

const decorateTasks = (
  tasks: ITask[],
  ctx: IDecorateContext = {},
): ITaskRow[] => {
  // One clock for the whole list, so two rows can never disagree about today.
  const now = ctx.now ?? new Date()
  return tasks.map(task => decorateTask(task, {...ctx, now}))
}

/** Groups the list the way the design reads it: open work first, then what is
 *  done, and inside a status the rows that need attention float up. The server
 *  already returns `dueDate ASC`, which is the final tie-break. */
const sortTaskRows = (rows: ITaskRow[]): ITaskRow[] =>
  [...rows].sort((a, b) => {
    const byStatus = STATUS_RANK[a.status] - STATUS_RANK[b.status]
    if (byStatus !== 0) return byStatus
    const byAttention = Number(b.isOverdue) - Number(a.isOverdue)
    if (byAttention !== 0) return byAttention
    return Number(b.isStale) - Number(a.isStale)
  })

const countByStatus = (rows: ITaskRow[]): Record<TTaskStatus, number> => {
  const counts = {
    [TASK_STATUS.pending]: 0,
    [TASK_STATUS.inProgress]: 0,
    [TASK_STATUS.completed]: 0,
    [TASK_STATUS.closed]: 0,
  }
  rows.forEach(row => {
    counts[row.status] += 1
  })
  return counts
}

/** Closed work is hidden from `all` — it is finished and not counted towards
 *  progress — and reachable through its own tab. */
const filterRowsByStatus = (
  rows: ITaskRow[],
  filter: TTaskStatusFilter,
): ITaskRow[] =>
  filter === 'all'
    ? rows.filter(row => row.status !== TASK_STATUS.closed)
    : rows.filter(row => row.status === filter)

const resolveMemberState = (rows: ITaskRow[]): TTeamMemberState => {
  if (rows.length === 0) return 'notStarted'
  const open = rows.filter(row => !row.isTerminal)
  if (open.length === 0) return 'allCompleted'
  if (open.some(row => row.isStale || row.isOverdue)) return 'needsUpdate'
  // Measured across the whole bucket, not just the open rows: someone who has
  // finished one task and left another pending has plainly started.
  if (rows.every(row => row.status === TASK_STATUS.pending)) return 'notStarted'
  return 'inProgress'
}

/**
 * Per-owner rollup for the "team this week" strip. Keyed on the owner's email
 * because that is the only identity a task carries — `responsible` is
 * denormalised JSON with no foreign key to a user row.
 *
 * Closed tasks are excluded: they were dropped, not delivered, so counting them
 * either way would misstate the person's week.
 */
const groupByOwner = (
  rows: ITaskRow[],
  unassignedName: string,
): ITeamMemberSummary[] => {
  const buckets = new Map<string, ITaskRow[]>()

  rows
    .filter(row => row.status !== TASK_STATUS.closed)
    .forEach(row => {
      const key = row.ownerEmail || UNASSIGNED_OWNER_KEY
      const bucket = buckets.get(key)
      if (bucket) bucket.push(row)
      else buckets.set(key, [row])
    })

  const summaries: ITeamMemberSummary[] = [...buckets.entries()].map(
    ([key, ownerRows]) => {
      const completedCount = ownerRows.filter(
        row => row.status === TASK_STATUS.completed,
      ).length
      const isUnassigned = key === UNASSIGNED_OWNER_KEY
      const first = ownerRows[0]
      return {
        key,
        person: isUnassigned ? undefined : first.owner,
        name: isUnassigned ? unassignedName : first.ownerName,
        isMine: !isUnassigned && ownerRows.some(row => row.isMine),
        isUnassigned,
        totalCount: ownerRows.length,
        completedCount,
        pct: ownerRows.length
          ? Math.round((completedCount / ownerRows.length) * 100)
          : 0,
        state: resolveMemberState(ownerRows),
      }
    },
  )

  // The signed-in user reads their own column first; Unassigned is a gap to
  // close rather than a person, so it sits last.
  return summaries
    .sort((a, b) => {
      if (a.isUnassigned !== b.isUnassigned) return a.isUnassigned ? 1 : -1
      if (a.isMine !== b.isMine) return a.isMine ? -1 : 1
      return a.name.localeCompare(b.name)
    })
    .slice(0, taskConstants.TEAM_STRIP_MAX_OWNERS)
}

/**
 * Turns a multi-line paste into the `tasksNames` array `POST /tasks/bulk`
 * expects. Trims, drops blanks, de-duplicates case-insensitively, and caps the
 * batch — the route itself validates none of that.
 */
const parseBulkTaskNames = (raw: string): string[] => {
  const seen = new Set<string>()
  const names: string[] = []
  raw.split(/\r?\n/).forEach(line => {
    const name = line.trim()
    if (!name) return
    const dedupeKey = name.toLowerCase()
    if (seen.has(dedupeKey)) return
    seen.add(dedupeKey)
    names.push(name)
  })
  return names.slice(0, taskConstants.MAX_BULK_TASKS)
}

/** The due date a task gets when it is created without one. */
const defaultDueDate = (now: Date = new Date()): Date => {
  const due = new Date(now)
  due.setDate(due.getDate() + taskConstants.DEFAULT_DUE_DAYS)
  return due
}

/** Trims, then cuts to `max` characters with a trailing ellipsis only when cut. */
const truncateText = (text: string, max: number): string => {
  const trimmed = text.trim()
  return trimmed.length > max ? `${trimmed.slice(0, max).trimEnd()}…` : trimmed
}

export const taskRowUtil = {
  normalizeTaskStatus,
  readOwner,
  decorateTask,
  decorateTasks,
  sortTaskRows,
  countByStatus,
  filterRowsByStatus,
  groupByOwner,
  parseBulkTaskNames,
  defaultDueDate,
  truncateText,
  UNASSIGNED_OWNER_KEY,
} as const
